// Redirect path. This chunk stays tiny: it parses `?q=`, builds the target URL
// and leaves. The React builder only loads when there's nothing to redirect,
// or when the query needs fixing.
import { CUSTOM_PREFIX } from './lib/custom';
import { lookupDdgBang } from './lib/ddg';
import { setCustomEngines, setEngineAliases } from './lib/engines';
import {
  ddgUrl,
  engineUrl,
  expandRepeat,
  interpret,
  isWebUrl,
  lastWords,
} from './lib/resolve';
import {
  loadCustomEngines,
  loadGroups,
  loadPresets,
  loadRecent,
  loadSettings,
  recordRecent,
} from './lib/storage';
import { describe, reportError, track, trackFirst } from './lib/track';

const BUILDER_PARAM = 'edit';

function openBuilder(q?: string, tabsBlocked = false) {
  track('visit');
  if (q !== undefined) {
    history.replaceState(
      null,
      '',
      `/?${BUILDER_PARAM}=${encodeURIComponent(q)}${tabsBlocked ? '&tabs=blocked' : ''}`
    );
  }
  void import('./main');
}

async function route(raw: string): Promise<void> {
  setCustomEngines(loadCustomEngines());
  const settings = loadSettings();
  setEngineAliases(settings.engineAliases);
  const ctx = {
    settings,
    presets: loadPresets(),
    groups: loadGroups(),
  };
  // `!!` repeats the last search's words; with none to repeat, the builder
  // explains why.
  const repeat = expandRepeat(raw, () =>
    ctx.settings.keepRecent ? lastWords(loadRecent().map(r => r.q)) : undefined
  );
  if (repeat.missing) return openBuilder(raw);
  const q = repeat.query;
  const result = interpret(q, ctx);
  // Any site can link here with `!!` in the search, so words from your
  // history only go straight to a built-in, non-AI engine. Anything else (a
  // DuckDuckGo bang, an AI chat, a group, your own engine) waits in the
  // builder for Enter.
  if (
    q !== raw &&
    !(
      result.kind === 'engine' &&
      !result.engine.id.startsWith(CUSTOM_PREFIX) &&
      result.engine.category !== 'AI'
    )
  )
    return openBuilder(raw);
  const go = (url: string) => {
    // Never navigate anywhere but the web, whatever a stored engine says.
    if (!isWebUrl(url)) return openBuilder(q);
    if (ctx.settings.keepRecent) recordRecent(q);
    track('search', describe(result));
    location.replace(url);
  };

  if (result.kind === 'engine' && result.errors.length === 0) {
    const url = engineUrl(
      result.engine,
      result.values,
      result.terms,
      ctx.settings
    );
    if (url.ok) return go(url.url);
  }

  if (result.kind === 'external' && result.name && ctx.settings.ddgFallback) {
    const name = [result.name, ...result.mods].join('.');
    const template = await lookupDdgBang(name);
    if (template) return go(ddgUrl(template, result.terms));
  }

  // A group opens its other sites in new tabs and this one goes to the first.
  // Browsers only allow that without a click once pop-ups are allowed for
  // this site; until then the builder opens, says so, and Enter opens them.
  if (result.kind === 'group') {
    const urls = result.members.flatMap(m => {
      if (!m.result.ok) return [];
      const url = engineUrl(
        m.result.engine,
        m.result.values,
        m.result.terms,
        ctx.settings
      );
      return url.ok && isWebUrl(url.url) ? [url.url] : [];
    });
    const [first, ...rest] = urls;
    if (first && urls.length === result.members.length) {
      // The first tab tells us whether pop-ups are allowed; if not, nothing
      // has opened yet.
      const tab = rest[0] ? window.open(rest[0], '_blank') : undefined;
      if (tab === null) return openBuilder(q, true);
      if (tab) tab.opener = null;
      for (const url of rest.slice(1)) {
        const w = window.open(url, '_blank');
        if (w) w.opener = null;
      }
      track('group', { s: result.group ? 'saved' : 'inline' });
      if (ctx.settings.keepRecent) recordRecent(q);
      return location.replace(first);
    }
  }

  // Anything we can't resolve cleanly goes to the builder, prefilled, so the
  // problem is shown rather than silently searching for the wrong thing.
  openBuilder(q);
}

// Errors in the site's own code, anywhere, by place only (see reportError).
window.addEventListener('error', e =>
  reportError('script', e.error, { ownCodeOnly: true })
);
window.addEventListener('unhandledrejection', e =>
  reportError('promise', e.reason, { ownCodeOnly: true })
);

// Registered here rather than in the builder, so someone who only ever
// searches from the address bar gets the cached, network-free page too.
if (import.meta.env.PROD && 'serviceWorker' in navigator)
  void navigator.serviceWorker.register('/sw.js').catch(() => undefined);

trackFirst();
const q = new URLSearchParams(location.search).get('q');
// Anything unexpected (a broken custom engine, a storage oddity) opens the
// builder with the query rather than leaving a blank page.
if (q?.trim())
  route(q).catch(error => {
    reportError('redirect', error);
    openBuilder(q);
  });
else openBuilder();
